import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/admin/PageHeader";
import ImageUploader from "@/components/admin/ImageUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CATEGORIES = ["Buying Guides", "Renting Guides", "Selling Guides", "Landlord Advice", "Property Investment", "Mortgages", "Moving Home", "Local Area Guides", "Property News", "Market Updates"];

export default function BlogForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !!id;
  const [form, setForm] = useState({ category: "Property News", state: "draft" });
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (editing) api.get(`/admin/blog/${id}`).then((r) => { setForm(r.data); setLoading(false); }).catch(() => navigate("/admin/blog")); }, [id, editing, navigate]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (state) => {
    if (!form.title) { toast.error("Add a title"); return; }
    setSaving(true);
    const payload = { ...form, state: state || form.state };
    try {
      let res;
      if (editing) res = await api.put(`/admin/blog/${id}`, payload);
      else res = await api.post("/admin/blog", payload);
      toast.success("Saved");
      if (!editing) navigate(`/admin/blog/${res.data.id}`); else setForm(res.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-[#C81D35] animate-spin" /></div>;

  return (
    <div>
      <PageHeader title={editing ? "Edit post" : "New post"}
        action={<div className="flex gap-2">
          <Link to="/admin/blog"><Button variant="outline" className="rounded-full gap-2"><ArrowLeft className="w-4 h-4" /> Back</Button></Link>
          <Button variant="outline" onClick={() => save("draft")} disabled={saving} className="rounded-full">Save draft</Button>
          <Button onClick={() => save("published")} disabled={saving} className="rounded-full bg-[#C81D35] hover:bg-[#a5162b] gap-2" data-testid="publish-post">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Publish</Button>
        </div>} />
      <div className="bg-white rounded-xl border border-border p-6 space-y-4 max-w-3xl">
        <div className="space-y-1.5"><Label>Title</Label><Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="post-title" /></div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="post-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1.5"><Label>Author</Label><Input value={form.author || ""} onChange={(e) => set("author", e.target.value)} /></div>
        </div>
        <div className="space-y-1.5"><Label>Excerpt</Label><Textarea rows={2} value={form.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} /></div>
        <ImageUploader images={form.image ? [{ url: form.image, is_featured: true }] : []} onChange={(imgs) => set("image", imgs[imgs.length - 1]?.url || "")} label="Featured image" />
        <div className="space-y-1.5"><Label>Content (HTML supported)</Label><Textarea rows={14} value={form.content || ""} onChange={(e) => set("content", e.target.value)} data-testid="post-content" /></div>
      </div>
    </div>
  );
}
